import * as React from "react";
import { Megaphone } from "lucide-react";
import { createFileRoute } from "@tanstack/react-router";
import { msg } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import type { MessageDescriptor } from "@lingui/core";

import {
  CompanionHero,
  SurfaceHeader,
  SurfaceLayout,
  cn,
} from "@ando/ui";

import {
  CHANGELOG_ENTRIES,
  type ChangelogEntry,
  type ChangelogTag,
  useChangelogStore,
} from "@/features/help";

/**
 * ``/settings/changelog`` — timeline of recent shipped changes.
 *
 * On mount we call ``markSeen()`` after a 500ms delay so the user has
 * time to register the orange "Novità" dot before it disappears. The
 * delay also covers the route transition animation — instantaneous
 * clearance would feel like the badge never actually existed.
 */
function ChangelogPage() {
  const { i18n } = useLingui();
  const markSeen = useChangelogStore((s) => s.markSeen);

  React.useEffect(() => {
    const t = window.setTimeout(() => markSeen(), 500);
    return () => window.clearTimeout(t);
  }, [markSeen]);

  return (
    <SurfaceLayout
      header={
        <SurfaceHeader
          label={i18n._(msg`Aiuto`)}
          title={i18n._(msg`Novità`)}
          meta={
            <span>
              <Trans>
                <strong>{CHANGELOG_ENTRIES.length} aggiornamenti</strong> · le
                ultime modifiche distribuite ad Ando
              </Trans>
            </span>
          }
        />
      }
      hero={
        <CompanionHero icon={<Megaphone size={14} strokeWidth={2} />}>
          <Trans>
            Tutto quello che è cambiato di recente. Le novità più rilevanti
            appaiono anche come <strong>nudge proattivi</strong> in chat.
          </Trans>
        </CompanionHero>
      }
    >
      <ol className="relative pt-4 pl-6">
        {/* Vertical timeline rail */}
        <span
          aria-hidden
          className="absolute top-3 bottom-3 left-2 w-px bg-hairline"
        />
        {CHANGELOG_ENTRIES.map((entry) => (
          <TimelineRow key={entry.date + entry.title.id} entry={entry} />
        ))}
      </ol>
    </SurfaceLayout>
  );
}

// ── Row ─────────────────────────────────────────────────────────────────

const TAG_STYLES: Record<
  ChangelogTag,
  { label: MessageDescriptor; chip: string }
> = {
  feature: {
    label: msg`Nuovo`,
    chip: "bg-accent/10 text-accent border-accent/20",
  },
  improvement: {
    label: msg`Miglioramento`,
    chip: "bg-ando-orange/10 text-ando-orange border-ando-orange/20",
  },
  fix: {
    label: msg`Fix`,
    chip: "bg-success/10 text-success border-success/20",
  },
};

function TimelineRow({ entry }: { readonly entry: ChangelogEntry }) {
  const { i18n } = useLingui();
  const tag = TAG_STYLES[entry.tag];
  return (
    <li className="relative mb-6 last:mb-2">
      {/* Pallina */}
      <span
        aria-hidden
        className="-translate-x-1/2 absolute top-1.5 left-[-1rem] inline-block h-2.5 w-2.5 rounded-full bg-ando-orange shadow-[0_0_0_3px_var(--color-cream,#FAF8F4)]"
      />
      <header className="mb-1.5 flex flex-wrap items-baseline gap-2">
        <h2 className="font-semibold text-[14px] text-ink-1">{i18n._(entry.title)}</h2>
        <span
          className={cn(
            "inline-flex h-[18px] items-center rounded-full border px-1.5 font-medium text-[10px] uppercase tracking-wide",
            tag.chip,
          )}
        >
          {i18n._(tag.label)}
        </span>
        <time
          dateTime={entry.date}
          className="text-[11px] text-ink-3 tabular-nums"
        >
          {formatDateIt(entry.date)}
        </time>
      </header>
      <p className="max-w-prose text-[13px] text-ink-2 leading-[1.55]">
        {i18n._(entry.body)}
      </p>
    </li>
  );
}

/**
 * Format an ISO YYYY-MM-DD as the Italian "1 giugno 2026" long form.
 * Falls back to the raw input if Intl is unavailable (older runtimes).
 */
function formatDateIt(iso: string): string {
  try {
    const d = new Date(`${iso}T00:00:00`);
    return new Intl.DateTimeFormat("it-IT", {
      day: "numeric",
      month: "long",
      year: "numeric",
    }).format(d);
  } catch {
    return iso;
  }
}

export const Route = createFileRoute("/_authed/_modal/settings/changelog")({
  component: ChangelogPage,
});
